import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { UrlParams, PaginatedPayrollReportList } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUsePayrollReport {
  payrollPeriod?: string | null;
  ordering?: string;
  enabled?: boolean;
  csvExport?: string;
  limit?: string;
}

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey?: (string | undefined | string[] | null)[];
}

function usePayrollReport({
  payrollPeriod,
  ordering,
  enabled = true,
  csvExport,
  limit = '20',
}: IUsePayrollReport): UseInfiniteQueryResult<AxiosResponse<PaginatedPayrollReportList>> {
  const fetchPayrollReport = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedPayrollReportList>> => {
      const params = pageParam;

      if (!!payrollPeriod) {
        params.payroll_period = payrollPeriod;
      }
      if (ordering) {
        params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      }

      return client.get(`reports/payroll?${encodeUrlParams(params)}`);
    },
    [limit, ordering, payrollPeriod],
  );

  return useInfiniteQuery(['payroll', payrollPeriod, ordering, csvExport], fetchPayrollReport, {
    getNextPageParam: page => {
      return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
    },
    enabled: enabled && !!payrollPeriod,
    staleTime: 86400000, // 1 day
  });
}

export default usePayrollReport;
